import React from 'react';
import classes from './WeeklyViewEntry.module.css';
import { SalesOrderInfo } from '../../../store/slices/salesOrders';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import clockIcon from '../../../assets/icons/clock.svg';
import consultingIcon from '../../../assets/icons/consulting-icon.svg';
import diagnosticIcon from '../../../assets/icons/diagnostic-icon.svg';
import editIcon from '../../../assets/icons/edit-appointment.svg';
import { UpdateAppointmentModal } from './UpdateAppointmentModal';

import { useAppSelector } from '../../../store/store';
import { IDoctor, selectDoctors } from '../../../store/slices/doctors';
import { useTranslation } from '../../../hooks/useTranslations';

type Props = {
  salesOrders: SalesOrderInfo[];
  calendarType?: string;
  handleUpdateAppointment: (
    salesOrder: SalesOrderInfo,
    start: Date,
    end: Date,
    callback: () => void
  ) => void;
  loadingUpdate?: boolean;
};

const WeeklyViewEntry: React.FC<Props> = ({
  salesOrders,
  handleUpdateAppointment,
  loadingUpdate,
}) => {
  const { translations } = useTranslation();
  const navigate = useNavigate();
  const [updateAppointment, setUpdateAppointment] = React.useState<boolean>(false);

  const handleNavigation = (isDiagnostic: boolean, salesOrder: any) => {
    if (isDiagnostic) {
      navigate(
        `/web/schedule?selectedTab=check-in&accountNumber=${salesOrder.customer_person.person_account_number}&salesOrderId=${salesOrder.sales_order.sales_order_id}&salesOrderItemId=${salesOrder?.sales_order_item?.sales_order_item_id}`
      );
      return;
    }
    navigate(
      `/web/consultation/${salesOrder.sales_order.sales_order_id}/${salesOrder.sales_order_item?.sales_order_item_id}`
    );
    return;
  };
  const doctors: IDoctor[] = useAppSelector<IDoctor[]>(selectDoctors);
  const doctorAssignedFullName = (data: SalesOrderInfo) => {
    const tempoDoctorAssigned = doctors.find((doctor) =>
      doctor.person_appointments.find((appointment) => {
        if (data?.sales_order_item.sales_order_id && data?.sales_order_item.sales_order_item_id) {
          return (
            appointment.sales_order_id == data.sales_order_item.sales_order_id &&
            appointment.sales_order_item_id == data.sales_order_item.sales_order_item_id
          );
        }
      })
    );
    if (tempoDoctorAssigned) {
      return `${tempoDoctorAssigned.person_firstname} ${tempoDoctorAssigned.person_lastname}`;
    }
    return undefined;
  };
  const isWeeklyDiagnostic =
    salesOrders[0]?.service_service_group.service_group_id === 1 ||
    salesOrders[0]?.service_service_group.service_group_id === 4;
  return (
    <div className={classes.container}>
      {salesOrders.length > 1 ? (
        <>
          {Array(salesOrders.length)
            .fill(undefined)
            .map((n, index) => {
              const isDiagnostic =
                salesOrders[index]?.service_service_group.service_group_id === 1 ||
                salesOrders[index]?.service_service_group.service_group_id === 4;
              const isClosed =
                salesOrders[index]?.work_order?.work_order_status_id == 2 ||
                salesOrders[index]?.work_order?.work_order_status_id == 3;

              return (
                <div
                  key={index}
                  className={[
                    classes.smallWeeklyView,
                    isDiagnostic
                      ? classes.diagnostic
                      : doctorAssignedFullName(salesOrders[index])
                      ? classes.consultation
                      : classes.notAssigned,
                  ].join(' ')}
                >
                  {salesOrders[index] && (
                    <div
                      onClick={() => {
                        if (isClosed) return;
                        handleNavigation(isDiagnostic, salesOrders[index]);
                      }}
                      title={`${salesOrders[index].customer_person.person_firstname} ${salesOrders[index].customer_person.person_lastname}}`}
                      className={classes.smallWeeklyViewLink}
                    >
                      <div className={classes.personName}>
                        {salesOrders[index].customer_person.person_firstname}
                      </div>
                      <div className={classes.personName}>
                        {salesOrders[index].customer_person.person_lastname}
                      </div>
                      <div className={classes.clockIcon}>
                        <img alt={'clock'} src={clockIcon} className={classes.clockImage} />
                        <div className={classes.length}>
                          {moment(salesOrders[index].sales_order_appointment.end_time).diff(
                            moment(salesOrders[index].sales_order_appointment.start),
                            'minutes'
                          )}{' '}
                          min.
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
        </>
      ) : (
        <div
          className={[
            classes.largeWeeklyView,
            isWeeklyDiagnostic
              ? classes.diagnostic
              : doctorAssignedFullName(salesOrders[0])
              ? classes.consultation
              : classes.notAssigned,
          ].join(' ')}
        >
          <div
            onClick={() => {
              handleNavigation(isWeeklyDiagnostic, salesOrders[0]);
            }}
            className={classes.largeWeeklyViewLink}
          >
            <div className={classes.customerInfo}>
              {/* <Avatar /> */}
              <div
                className={classes.userInfo}
                title={`${salesOrders[0].customer_person.person_firstname} ${salesOrders[0].customer_person.person_lastname}`}
              >
                <div className={classes.customerName}>
                  {salesOrders[0].customer_person.person_firstname}
                </div>
                <div className={classes.customerName}>
                  {salesOrders[0].customer_person.person_lastname}
                </div>
              </div>
            </div>
            {!isWeeklyDiagnostic && (
              <div className={classes.doctorName}>
                {doctorAssignedFullName(salesOrders[0])
                  ? `${translations('label.assigned')} ${doctorAssignedFullName(salesOrders[0])}`
                  : translations('label_no.doctor.assigned')}
              </div>
            )}
            <div className={classes.clockIconLargeView}>
              <img alt={'clock'} src={clockIcon} />
              <div className={classes.length}>
                {moment(salesOrders[0].sales_order_appointment.start).format('HH:mm - ')}
                {moment(salesOrders[0].sales_order_appointment.end_time).format('HH:mm')}
              </div>
            </div>
            <div className={classes.service} title={salesOrders[0].service.service_name}>
              {isWeeklyDiagnostic ? (
                <img alt={'dir'} src={diagnosticIcon} />
              ) : (
                <img alt={'dir'} src={consultingIcon} />
              )}
              <div className={classes.serviceName}>{salesOrders[0].service.service_name}</div>
            </div>
            <div
              className={classes.editIcon}
              onClick={(event) => {
                event.stopPropagation();
                setUpdateAppointment(true);
              }}
            >
              <img alt={'edit'} src={editIcon} />
              <div className={classes.serviceName}>{translations('edit')}</div>
            </div>
          </div>
          {updateAppointment && (
            <UpdateAppointmentModal
              salesOrder={salesOrders[0]}
              isOpen={updateAppointment}
              onClose={() => setUpdateAppointment(false)}
              handleUpdateAppointment={(start, end) => {
                handleUpdateAppointment(salesOrders[0], start, end, () =>
                  setUpdateAppointment(false)
                );
              }}
              loadingUpdate={!!loadingUpdate}
            />
          )}
        </div>
      )}
    </div>
  );
};

export { WeeklyViewEntry };
